import React, { useEffect, useState } from 'react';
import { X, Wallet } from 'lucide-react';
import Button from '../common/Button.js';
import Alert from '../common/Alert';
import cashbookService from '../../services/CashbookService.js';

const getToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const CashbookOpeningBalanceModal = ({ open, onClose, onSaved }) => {
  const [openingCash, setOpeningCash] = useState('');
  const [openingBank, setOpeningBank] = useState('');
  const [asOfDate, setAsOfDate] = useState(getToday());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    cashbookService
      .getOpeningBalance()
      .then((res) => {
        if (!active) return;
        const data = res.data?.data;
        if (data) {
          setOpeningCash(String(data.opening_cash ?? ''));
          setOpeningBank(String(data.opening_bank ?? ''));
          setAsOfDate(data.as_of_date || getToday());
        } else {
          setOpeningCash('');
          setOpeningBank('');
          setAsOfDate(getToday());
        }
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cashbookService.setOpeningBalance({
        opening_cash: parseFloat(openingCash) || 0,
        opening_bank: parseFloat(openingBank) || 0,
        as_of_date: asOfDate,
      });
      Alert.success('Opening balance saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save opening balance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-buttonBg" /> Opening Balance
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Set the starting cash & bank balance. It carries forward automatically each day.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opening Cash (₹)</label>
            <input
              type="number" step="0.01" value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              placeholder="0.00" disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opening Bank (₹)</label>
            <input
              type="number" step="0.01" value={openingBank}
              onChange={(e) => setOpeningBank(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              placeholder="0.00" disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
            <input
              type="date" value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              disabled={loading}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={saving} disabled={saving || loading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashbookOpeningBalanceModal;
